import React from 'react';
import { useAtom } from 'jotai';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';

import { AI_MODELS_DEFAULT_PER_PAGE, aiModelsHydratedAtom, aiModelsListParamsAtom } from '@/store/aiModels';

export interface AiModelsSearchFields {
  search: string;
  is_active: 'all' | 'true' | 'false';
}

export function useAiModelsFilters() {
  const [params, setParams] = useAtom(aiModelsListParamsAtom);
  const [, setHydrated] = useAtom(aiModelsHydratedAtom);

  const [queryParams, setQueryParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    per_page: parseAsInteger.withDefault(AI_MODELS_DEFAULT_PER_PAGE),
    sort: parseAsString,
    search: parseAsString.withDefault(''),
    is_active: parseAsString.withDefault('all')
  });

  React.useEffect(() => {
    const filters: Record<string, string | undefined> = {};
    if (queryParams.search) filters.search = queryParams.search;
    if (queryParams.is_active !== 'all') filters.is_active = queryParams.is_active;

    setParams((prev) => {
      const next = {
        ...prev,
        page: queryParams.page,
        per_page: queryParams.per_page,
        sort: queryParams.sort || undefined,
        filters
      };

      if (
        prev.page === next.page &&
        prev.per_page === next.per_page &&
        prev.sort === next.sort &&
        JSON.stringify(prev.filters) === JSON.stringify(next.filters)
      ) {
        return prev;
      }

      return next;
    });

    setHydrated(true);
  }, [
    queryParams.page,
    queryParams.per_page,
    queryParams.sort,
    queryParams.search,
    queryParams.is_active,
    setParams,
    setHydrated
  ]);

  const searchFields: AiModelsSearchFields = {
    search: queryParams.search,
    is_active: (queryParams.is_active as AiModelsSearchFields['is_active']) || 'all'
  };

  const setSearchFields = React.useCallback(
    (updater: (prev: AiModelsSearchFields) => AiModelsSearchFields) => {
      const next = updater(searchFields);
      setQueryParams({
        search: next.search || null,
        is_active: next.is_active === 'all' ? null : next.is_active,
        page: 1
      });
    },
    [searchFields, setQueryParams]
  );

  const clearFilters = React.useCallback(() => {
    setQueryParams({
      search: null,
      is_active: null,
      page: 1
    });
  }, [setQueryParams]);

  const handlePageChange = React.useCallback(
    (page: number) => {
      setParams((prev) => ({ ...prev, page }));
      setQueryParams({ page });
    },
    [setParams, setQueryParams]
  );

  const handlePerPageChange = React.useCallback(
    (perPage: number) => {
      setParams((prev) => ({ ...prev, page: 1, per_page: perPage }));
      setQueryParams({ per_page: perPage, page: 1 });
    },
    [setParams, setQueryParams]
  );

  const sortParam = params.sort;
  const setSortParam = React.useCallback(
    (sortValue: string | null) => {
      setParams((prev) => ({ ...prev, page: 1, sort: sortValue || undefined }));
      setQueryParams({ sort: sortValue || null, page: 1 });
    },
    [setParams, setQueryParams]
  );

  const hasAnyFilter = Boolean(queryParams.search || queryParams.is_active !== 'all');

  return {
    params,
    searchFields,
    setSearchFields,
    clearFilters,
    handlePageChange,
    handlePerPageChange,
    hasAnyFilter,
    sortParam,
    setSortParam
  };
}
